import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Stack, Chip,
} from '@mui/material';
import {
  Storefront, LocalShipping, TrendingUp, AttachMoney, CheckCircle,
} from '@mui/icons-material';
import { ezpPalette } from '../../components/EzpLayout';

export default function EzpPartner() {
  const navigate = useNavigate();
  const { t } = useTranslation('ezp');

  const benefits = [
    { icon: <AttachMoney sx={{ fontSize: 36 }} />, title: t('partner.benefit1_title', '零庫存壓力'), desc: t('partner.benefit1_desc', '不需要備貨、不需要倉庫。從 EZP 選品後直接上架，客人下單由 EZP 出貨。') },
    { icon: <Storefront sx={{ fontSize: 36 }} />, title: t('partner.benefit2_title', '免額外費用'), desc: t('partner.benefit2_desc', 'URStore 不收取 EZP 供貨的額外費用。你只需要付 URStore SaaS 月費，EZP 賺經銷價差。') },
    { icon: <TrendingUp sx={{ fontSize: 36 }} />, title: t('partner.benefit3_title', '高利潤空間'), desc: t('partner.benefit3_desc', '建議售價倍率 1.4x，每件商品預估 28-40% 利潤率。你可以自由訂價。') },
    { icon: <LocalShipping sx={{ fontSize: 36 }} />, title: t('partner.benefit4_title', '直運出貨'), desc: t('partner.benefit4_desc', '客人下單後，EZP 直接出貨給客人。你不需要碰貨、包裝、寄件。') },
  ];

  const faqs = [
    { q: t('partner.faq1_q', '我需要付什麼費用？'), a: t('partner.faq1_a', '你只需要付 URStore SaaS 月費（$599 起）。EZP 商品匯入和瀏覽完全免費，EZP 賺取的是經銷價差。') },
    { q: t('partner.faq2_q', '我可以自訂售價嗎？'), a: t('partner.faq2_a', '可以。匯入時可調整售價倍率，匯入後也可以在後台自由修改每件商品的價格。') },
    { q: t('partner.faq3_q', '出貨速度多快？'), a: t('partner.faq3_a', 'EZP 一般在 1-3 個工作天內出貨，依商品庫存狀況而定。') },
    { q: t('partner.faq4_q', '有退換貨機制嗎？'), a: t('partner.faq4_a', '依 EZP 退換貨政策辦理，URStore 協助聯繫處理。') },
  ];

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${ezpPalette.primary} 0%, #2E7D32 100%)`, color: 'white', py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Chip label={t('partner.badge', 'EZP 特約供貨')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, mb: 3 }} />
          <Typography variant="h3" sx={{ fontWeight: 950, mb: 2, fontSize: { xs: '1.8rem', md: '2.8rem' } }}>
            {t('partner.title', '沒有貨也能開店')}
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontWeight: 400, lineHeight: 1.8 }}>
            {t('partner.subtitle', 'EZP 好批提供 2,700+ 商品，一鍵上架到你的 URStore 商店。顧客下單後由 EZP 直接出貨，你只需要專心經營行銷。')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" href="https://urstore.tw/apply" target="_blank"
              sx={{ bgcolor: ezpPalette.secondary, fontWeight: 900, borderRadius: 2, px: 5, py: 1.5, '&:hover': { bgcolor: '#E65100' } }}>
              {t('partner.cta_open', '立即免費開店')}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/products')}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', fontWeight: 700, borderRadius: 2, px: 5, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              {t('partner.cta_browse', '先看商品')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Benefits */}
      <Box sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 950, textAlign: 'center', mb: 6 }}>
            {t('partner.benefits_title', '為什麼選擇 EZP 供貨？')}
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((b, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card elevation={0} sx={{ height: '100%', border: `1px solid ${ezpPalette.border}`, borderRadius: 3, p: 1 }}>
                  <CardContent>
                    <Box sx={{ color: ezpPalette.primary, mb: 2 }}>{b.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{b.title}</Typography>
                    <Typography variant="body2" sx={{ color: ezpPalette.textMuted, lineHeight: 1.8 }}>{b.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How the money flows */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 950, textAlign: 'center', mb: 2 }}>
            {t('partner.flow_title', '收費方式')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', color: ezpPalette.textMuted, mb: 5 }}>
            {t('partner.flow_subtitle', '簡單透明，三方共贏')}
          </Typography>

          <Grid container spacing={3}>
            {[
              { role: t('partner.you', '你（賣家）'), items: [t('partner.you_1', '付 URStore 月費 $599 起'), t('partner.you_2', '以經銷價從 EZP 進貨'), t('partner.you_3', '自訂售價，賺取價差')] },
              { role: 'URStore', items: [t('partner.ur_1', '收月費（$599/$1,299/$2,999）'), t('partner.ur_2', '0% 交易抽成'), t('partner.ur_3', '提供開店平台與工具')] },
              { role: 'EZP', items: [t('partner.ezp_1', '賺經銷價差'), t('partner.ezp_2', '負責出貨給顧客'), t('partner.ezp_3', '提供 2,700+ 商品')] },
            ].map((party, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card elevation={0} sx={{ height: '100%', border: `1px solid ${ezpPalette.border}`, borderRadius: 3, textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: ezpPalette.primary }}>{party.role}</Typography>
                  <Stack spacing={1}>
                    {party.items.map((item, j) => (
                      <Stack key={j} direction="row" spacing={1} alignItems="center">
                        <CheckCircle sx={{ fontSize: 18, color: ezpPalette.accent }} />
                        <Typography variant="body2">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: ezpPalette.bg }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 950, textAlign: 'center', mb: 5 }}>
            {t('partner.faq_title', '常見問題')}
          </Typography>
          <Stack spacing={2}>
            {faqs.map((faq, i) => (
              <Card key={i} elevation={0} sx={{ border: `1px solid ${ezpPalette.border}`, borderRadius: 2, p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{faq.q}</Typography>
                <Typography variant="body2" sx={{ color: ezpPalette.textMuted }}>{faq.a}</Typography>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: { xs: 7, md: 9 }, background: `linear-gradient(135deg, ${ezpPalette.primary} 0%, #2E7D32 100%)`, color: 'white', textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ fontWeight: 950, mb: 2 }}>
            {t('partner.final_title', '準備好了嗎？')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4 }}>
            {t('partner.final_desc', '14 天免費試用，不綁約。先把店開起來，再從 EZP 選品開始賣。')}
          </Typography>
          <Button variant="contained" size="large" href="https://urstore.tw/apply" target="_blank"
            sx={{ bgcolor: ezpPalette.secondary, fontWeight: 900, borderRadius: 2, px: 5, py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#E65100' } }}>
            {t('partner.final_cta', '立即免費開店')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
